import React, { createContext, useState, useContext, useEffect } from 'react';

const THEMES = {
  "midnight-blue": {
    name: "Midnight Blue",
    bg: "#1a1d2e",
    card: "#1e293b",
    cardHover: "#253347",
    border: "#334155",
    accent: "#3b82f6",
    accentHover: "#2563eb",
    accentGlow: "rgba(59,130,246,0.2)",
    live: "#10b981",
    text: "#f1f5f9",
    textMuted: "#94a3b8",
    textDim: "#64748b",
    inputBg: "#1e293b",
  },
  "electric-cyan": {
    name: "Electric Cyan",
    bg: "#0a1628",
    card: "#0f2035",
    cardHover: "#152a42",
    border: "#1e3a55",
    accent: "#22d3ee",
    accentHover: "#06b6d4",
    accentGlow: "rgba(34,211,238,0.25)",
    live: "#a3e635",
    text: "#ecfeff",
    textMuted: "#7dd3e8",
    textDim: "#3a8a9e",
    inputBg: "#0f2035",
  },
  "emerald-dark": {
    name: "Emerald Dark",
    bg: "#0f1a14",
    card: "#152b1e",
    cardHover: "#1c3828",
    border: "#2d4a37",
    accent: "#10b981",
    accentHover: "#059669",
    accentGlow: "rgba(16,185,129,0.2)",
    live: "#3b82f6",
    text: "#ecfdf5",
    textMuted: "#86cfa8",
    textDim: "#4a8a6a",
    inputBg: "#152b1e",
  },
  "amber-glow": {
    name: "Amber Glow",
    bg: "#1a1608",
    card: "#2a2410",
    cardHover: "#3a3218",
    border: "#4a4020",
    accent: "#f59e0b",
    accentHover: "#d97706",
    accentGlow: "rgba(245,158,11,0.2)",
    live: "#10b981",
    text: "#fef3c7",
    textMuted: "#c0a060",
    textDim: "#806838",
    inputBg: "#2a2410",
  },
  "royal-purple": {
    name: "Royal Purple",
    bg: "#16101e",
    card: "#221832",
    cardHover: "#2e2040",
    border: "#3d2a55",
    accent: "#a855f7",
    accentHover: "#9333ea",
    accentGlow: "rgba(168,85,247,0.2)",
    live: "#f43f5e",
    text: "#f3e8ff",
    textMuted: "#a78bbc",
    textDim: "#6e508a",
    inputBg: "#221832",
  },
  "ocean-teal": {
    name: "Ocean Teal",
    bg: "#0c1a1e",
    card: "#132a30",
    cardHover: "#1a3840",
    border: "#254a52",
    accent: "#06b6d4",
    accentHover: "#0891b2",
    accentGlow: "rgba(6,182,212,0.2)",
    live: "#f97316",
    text: "#ecfeff",
    textMuted: "#7cc5d4",
    textDim: "#4a8a98",
    inputBg: "#132a30",
  },
  "neon-lime": {
    name: "Neon Lime",
    bg: "#101410",
    card: "#1a2218",
    cardHover: "#222e20",
    border: "#334430",
    accent: "#84cc16",
    accentHover: "#65a30d",
    accentGlow: "rgba(132,204,22,0.25)",
    live: "#38bdf8",
    text: "#f0fdf4",
    textMuted: "#a3c890",
    textDim: "#688858",
    inputBg: "#1a2218",
  },
  "light-coral": {
    name: "Coral",
    bg: "#fff5f5",
    card: "#ffffff",
    cardHover: "#fff0f0",
    border: "#fecaca",
    accent: "#f43f5e",
    accentHover: "#e11d48",
    accentGlow: "rgba(244,63,94,0.12)",
    live: "#059669",
    text: "#1e1b1b",
    textMuted: "#6b5555",
    textDim: "#a08080",
    inputBg: "#fff0f0",
  },
  "light-fuchsia": {
    name: "Fuchsia",
    bg: "#fdf4ff",
    card: "#ffffff",
    cardHover: "#fae8ff",
    border: "#e9b0f5",
    accent: "#c026d3",
    accentHover: "#a21caf",
    accentGlow: "rgba(192,38,211,0.12)",
    live: "#0891b2",
    text: "#1c1017",
    textMuted: "#6d4a6d",
    textDim: "#a07aa0",
    inputBg: "#fae8ff",
  },
  "light-orange": {
    name: "Orange",
    bg: "#fffbf0",
    card: "#ffffff",
    cardHover: "#fff4e0",
    border: "#fed7aa",
    accent: "#ea580c",
    accentHover: "#c2410c",
    accentGlow: "rgba(234,88,12,0.12)",
    live: "#0d9488",
    text: "#1c1408",
    textMuted: "#78592e",
    textDim: "#a88855",
    inputBg: "#fff4e0",
  },
  "light-beige": {
    name: "Beige",
    bg: "#faf8f5",
    card: "#ffffff",
    cardHover: "#f5f0e8",
    border: "#e0d5c5",
    accent: "#92400e",
    accentHover: "#78350f",
    accentGlow: "rgba(146,64,14,0.12)",
    live: "#166534",
    text: "#1c1810",
    textMuted: "#6b5e4a",
    textDim: "#a09580",
    inputBg: "#f5f0e8",
  },
};

const ThemeContext = createContext(null);

export const ThemeProvider = ({ children }) => {
  const [themeId, setThemeId] = useState(() => {
    const saved = localStorage.getItem('gnh_theme');
    // Migrate old themes that were removed
    if (saved === 'crimson-night' || saved === 'slate-rose') return 'midnight-blue';
    return saved || 'midnight-blue';
  });

  const theme = THEMES[themeId] || THEMES["midnight-blue"];

  useEffect(() => {
    localStorage.setItem('gnh_theme', themeId);
    const root = document.documentElement;
    root.style.setProperty('--color-bg', theme.bg);
    root.style.setProperty('--color-card', theme.card);
    root.style.setProperty('--color-card-hover', theme.cardHover);
    root.style.setProperty('--color-border', theme.border);
    root.style.setProperty('--color-accent', theme.accent);
    root.style.setProperty('--color-accent-hover', theme.accentHover);
    root.style.setProperty('--color-accent-glow', theme.accentGlow);
    root.style.setProperty('--color-live', theme.live);
    root.style.setProperty('--color-text', theme.text);
    root.style.setProperty('--color-text-muted', theme.textMuted);
    root.style.setProperty('--color-text-dim', theme.textDim);
    root.style.setProperty('--color-input-bg', theme.inputBg);
  }, [themeId, theme]);

  return (
    <ThemeContext.Provider value={{ themeId, setThemeId, theme, themes: THEMES }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within ThemeProvider');
  return context;
};
